/**
 * API client for manual measurement entry.
 *
 * Uses apiClient() directly to match codebase patterns.
 * Target ranges fetched from existing /target-ranges endpoint.
 */
import { apiClient } from './client';
import { buildApiUrl } from './config';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CACHE_TIMES } from '@/config/cacheTimes';

/**
 * Tester brands available for selection
 */
export const TESTER_BRANDS = [
  { value: 'api', label: 'API' },
  { value: 'hanna', label: 'Hanna' },
  { value: 'nyos', label: 'NYOS' },
  { value: 'red_sea', label: 'Red Sea' },
  { value: 'salifert', label: 'Salifert' },
  { value: 'other', label: 'Other' },
] as const;

export type TesterBrand = (typeof TESTER_BRANDS)[number]['value'];

/**
 * Request to create a manual measurement
 */
export interface ManualMeasurementRequest {
  parameter_key: string;
  value: number;
  recorded_at: string; // ISO 8601
  notes?: string;
  tester_brand?: TesterBrand;
}

/**
 * Response from creating a measurement
 */
export interface ManualMeasurementResponse {
  id: number;
  parameter_key: string;
  value: number;
  recorded_at: string;
  source: 'manual';
}

/**
 * Target range from existing /target-ranges endpoint
 */
export interface TargetRangeInfo {
  parameterId: string;
  parameterName: string;
  unit: string;
  minValue: number | null;
  maxValue: number | null;
  enabled: boolean;
}

/**
 * Parameter target for ValueInput component
 */
export interface ParameterTarget {
  parameter_key: string;
  low: number | null;
  high: number | null;
  unit: string;
}

/**
 * Create a single manual measurement
 */
export async function createManualMeasurement(
  tankId: string,
  measurement: ManualMeasurementRequest
): Promise<ManualMeasurementResponse> {
  const url = buildApiUrl(`/api/tanks/${tankId}/measurements`);

  const response = await apiClient(url, {
    method: 'POST',
    body: JSON.stringify(measurement),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get user's target ranges for the tank
 * Uses existing /target-ranges endpoint
 */
export async function getTankTargetRanges(
  tankId: string
): Promise<{ ranges: TargetRangeInfo[] }> {
  const url = buildApiUrl(`/api/tanks/${tankId}/target-ranges`);

  const response = await apiClient(url, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * React Query key for tank target ranges. The cache under this key always holds
 * the full server response (disabled ranges included); every reader goes
 * through useTankTargetRanges so they all see the same filtered view.
 */
export const tankTargetRangesQueryKey = (tankId: string | undefined) =>
  ['tank-target-ranges', tankId];

/** A disabled range is switched off in Settings, so nothing should draw or score against it. */
function selectEnabledRanges(data: { ranges: TargetRangeInfo[] }): { ranges: TargetRangeInfo[] } {
  return { ranges: data.ranges.filter((r) => r.enabled) };
}

/**
 * Options for useTankTargetRanges hook
 */
export interface UseTankTargetRangesOptions {
  /** Additional condition for enabling the query (default: true) */
  enabled?: boolean;
}

/**
 * Hook to get the enabled target ranges for a tank.
 *
 * Not persisted to localStorage, and refreshed by TargetRangesForm on save, so
 * the long CONFIG staleTime never serves ranges the user has just edited.
 */
export function useTankTargetRanges(
  tankId: string | undefined,
  options: UseTankTargetRangesOptions = {}
) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: tankTargetRangesQueryKey(tankId),
    queryFn: () => getTankTargetRanges(tankId!),
    select: selectEnabledRanges,
    enabled: !!tankId && enabled,
    staleTime: CACHE_TIMES.CONFIG,
  });
}

/**
 * Hook to create a manual measurement
 */
export function useCreateMeasurement(tankId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (measurement: ManualMeasurementRequest) =>
      createManualMeasurement(tankId!, measurement),
    onSuccess: () => {
      // Invalidate relevant queries to refresh charts
      queryClient.invalidateQueries({ queryKey: ['sensors', tankId] });
      queryClient.invalidateQueries({ queryKey: ['water-tests', tankId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', tankId] });
      queryClient.invalidateQueries({ queryKey: ['latest-metrics', tankId] });
    },
  });
}

// =============================================================================
// Manual Entry Parameters (device-independent)
// =============================================================================

/**
 * Parameter info for manual entry
 */
export interface ManualEntryParameterInfo {
  key: string;
  display_name: string;
  unit: string;
  category: string;
  precision: number;
  default_min: number | null;
  default_max: number | null;
}

/**
 * Response from manual entry parameters endpoint
 */
export interface ManualEntryParametersResponse {
  parameters: ManualEntryParameterInfo[];
}

/**
 * Get parameters available for manual entry.
 * This returns a fixed set of parameters regardless of device configuration.
 */
export async function getManualEntryParameters(): Promise<ManualEntryParametersResponse> {
  const url = buildApiUrl('/api/manual-entry-parameters');

  const response = await apiClient(url, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new Error(
      error.detail || `HTTP ${response.status}: ${response.statusText}`
    );
  }

  return response.json();
}

/**
 * React Query key for manual entry parameters
 */
export const manualEntryParametersQueryKey = () => ['manual-entry-parameters'];

/**
 * Hook to get parameters available for manual entry.
 * These are device-independent and always available (pH, Sal, Alk, Ca, Mg, NO3, PO4).
 */
export function useManualEntryParameters() {
  return useQuery({
    queryKey: manualEntryParametersQueryKey(),
    queryFn: getManualEntryParameters,
    staleTime: CACHE_TIMES.STATIC, // these never change
  });
}

// =============================================================================
// Default Testers (per parameter, based on most recent manual entry)
// =============================================================================

/**
 * Response from default testers endpoint
 */
export interface DefaultTestersResponse {
  default_testers: Record<string, TesterBrand>;
}

/**
 * Get default tester brands for a tank based on most recent manual entries.
 */
export async function getDefaultTesters(
  tankId: string
): Promise<DefaultTestersResponse> {
  const url = buildApiUrl(`/api/tanks/${tankId}/default-testers`);

  const response = await apiClient(url, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * React Query key for default testers
 */
export const defaultTestersQueryKey = (tankId: string | undefined) =>
  ['default-testers', tankId];

/**
 * Hook to get default tester brands for a tank.
 * Used by FAB to prefill tester selection based on last manual entry.
 */
export function useDefaultTesters(tankId: string | undefined) {
  return useQuery({
    queryKey: defaultTestersQueryKey(tankId),
    queryFn: () => getDefaultTesters(tankId!),
    enabled: !!tankId,
    staleTime: CACHE_TIMES.LIVE, // refresh on each modal open is fine
  });
}

// =============================================================================
// Update Manual Measurements
// =============================================================================

/**
 * Request to update a manual measurement
 *
 * Tester handling:
 * - clear_tester=true: Remove tester from measurement
 * - tester_brand provided: Set tester to that value
 * - Neither: No change to tester
 */
export interface UpdateManualMeasurementRequest {
  value?: number;
  recorded_at?: string; // ISO 8601
  tester_brand?: TesterBrand;
  clear_tester?: boolean;
  note?: string;  // undefined = no change, empty string = clear note
}

/**
 * Response from updating a manual measurement
 */
export interface UpdateManualMeasurementResponse {
  id: number;
  value: number;
  recorded_at: string | null;
  tester_brand: string | null;
  note: string | null;
  updated_at: string;
}

/**
 * Update a manual measurement's value and/or tester brand.
 */
export async function updateManualMeasurement(
  tankId: string,
  measurementId: number,
  update: UpdateManualMeasurementRequest
): Promise<UpdateManualMeasurementResponse> {
  const url = buildApiUrl(`/api/tanks/${tankId}/measurements/${measurementId}`);

  const response = await apiClient(url, {
    method: 'PATCH',
    body: JSON.stringify(update),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Hook to update a manual measurement
 */
export function useUpdateManualMeasurement(tankId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ measurementId, update }: { measurementId: number; update: UpdateManualMeasurementRequest }) =>
      updateManualMeasurement(tankId!, measurementId, update),
    onSuccess: () => {
      // Invalidate data editor queries to refresh the table
      queryClient.invalidateQueries({ queryKey: ['outliers'] });
      // Invalidate chart data - use correct query key prefix
      queryClient.invalidateQueries({ queryKey: ['sensors', tankId] });
      queryClient.invalidateQueries({ queryKey: ['water-tests', tankId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', tankId] });
      // Invalidate FAB prefill and summary tiles after tester/value edit
      queryClient.invalidateQueries({ queryKey: ['default-testers', tankId] });
      queryClient.invalidateQueries({ queryKey: ['latest-metrics', tankId] });
    },
  });
}
